import { useMemo } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, RefreshCw, Trash2 } from "lucide-react";
import { VMList } from "@/components/vm/VMList";
import { useVMs, useStartVM, useStopVM, useRestartVM, useDeleteVM } from "@/hooks/useVMs";
import { useTenants } from "@/hooks/useTenants";
import { useAuth } from "@/auth/useAuth";

export default function VirtualMachines() {
  const { data: vms, isLoading, refetch } = useVMs();
  const { isAdmin } = useAuth();
  // Admins see VMs across tenants (super-admin scope on backend) — load
  // the tenant list once and pass an id→{slug,name} map to VMList so the
  // extra "Tenant" column resolves locally without an N+1 fetch.
  const { data: tenants } = useTenants();
  const tenantsById = useMemo(() => {
    if (!isAdmin || !tenants) return undefined;
    return new Map(tenants.map((t) => [t.id, { slug: t.slug, name: t.name }]));
  }, [isAdmin, tenants]);
  const startVM = useStartVM();
  const stopVM = useStopVM();
  const restartVM = useRestartVM();
  const deleteVM = useDeleteVM();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">
            Virtual Machines
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage your virtual machines across the cluster
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <Link
            to="/vms/trash"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800"
          >
            <Trash2 className="h-4 w-4" />
            Trash
          </Link>
          <Link
            to="/provisioning"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <PlusCircle className="h-4 w-4" />
            New VM
          </Link>
        </div>
      </div>

      <VMList
        vms={vms ?? []}
        isLoading={isLoading}
        onStart={(vmId) => startVM.mutate(vmId)}
        onStop={(vmId) => stopVM.mutate(vmId)}
        onRestart={(vmId) => restartVM.mutate(vmId)}
        onDelete={(vmId) => deleteVM.mutate(vmId)}
        tenantsById={tenantsById}
      />
    </div>
  );
}
